import { useParams } from 'react-router-dom';
import { COMPARE_COLOR, originColor, painelApi } from '@/api/painel';
import { CompareBars } from '@/components/painel/charts/CompareBars';
import { Donut } from '@/components/painel/charts/Donut';
import { StackedBars } from '@/components/painel/charts/StackedBars';
import { ErrorBox, EstimatedTag, Kpi, SectionTitle } from '@/components/painel/Kpi';
import { PeriodPicker } from '@/components/painel/PeriodPicker';
import { useLoad, usePainelPeriod } from '@/components/painel/PainelContext';
import { fmtBrl, fmtInt, fmtKwhNum, fmtMonth } from '@/components/painel/format';

/**
 * Financeiro v1: receita BRUTA e kWh por origem, por carregador e por mês, com
 * o período anterior ao lado. Sem repasse e sem resultado do ativo (dependem do
 * modelo comercial — fora da v1).
 */
export default function PainelFinanceiro() {
  const { orgId = '' } = useParams();
  const [period, setPeriod] = usePainelPeriod();
  const key = JSON.stringify(period);
  const fin = useLoad(() => painelApi.financeiro(orgId, period), [orgId, key]);
  const series = useLoad(() => painelApi.series(orgId, period, 'mes'), [orgId, key]);
  const f = fin.data;
  const a = f?.anterior ?? null;
  const parcial = f?.anterior_parcial ?? false;

  return (
    <div className="pt-1">
      <PeriodPicker value={period} onChange={setPeriod} label={f?.period.label} />
      {fin.error && !f && <ErrorBox message={fin.error} onRetry={fin.reload} />}
      {f && (
        <div className={fin.loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          <div className="mt-4 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
            <div className="col-span-2">
              <Kpi
                hero
                label="Receita bruta"
                value={fmtBrl(f.receita_total_brl)}
                delta={{ atual: Number(f.receita_total_brl), anterior: a && Number(a.receita_total_brl), parcial }}
                note={a ? `Período anterior ${fmtBrl(a.receita_total_brl)}${parcial ? ' (parcial)' : ''}` : undefined}
              />
            </div>
            <Kpi
              label="Ticket médio"
              value={fmtBrl(f.ticket_medio_brl)}
              delta={{ atual: Number(f.ticket_medio_brl), anterior: a && Number(a.ticket_medio_brl), parcial }}
              note={`${fmtInt(f.recargas)} recargas`}
            />
            <Kpi
              label="Energia"
              value={fmtKwhNum(f.kwh_total)}
              unit="kWh"
              delta={{ atual: f.kwh_total, anterior: a?.kwh_total, parcial }}
            />
          </div>

          <div className="grid gap-x-2.5 lg:grid-cols-2">
            <div>
              <SectionTitle>Receita por origem</SectionTitle>
              <div className="rounded-3xl bg-white p-4 shadow-soft">
                <Donut
                  ariaLabel="Receita por origem"
                  centerLabel="no período"
                  formatValue={(v) => fmtBrl(v)}
                  items={f.totais.map((t) => ({
                    key: t.origem,
                    label: t.label,
                    value: Number(t.receita_brl),
                    color: originColor(t.origem),
                    detail: `${fmtKwhNum(t.kwh)} kWh${t.estimada ? ' · receita estimada' : ''}`
                  }))}
                />
              </div>
            </div>
            <div>
              <SectionTitle>Receita por carregador</SectionTitle>
              <div className="rounded-3xl bg-white p-4 shadow-soft">
                <CompareBars
                  ariaLabel="Receita por carregador, período atual e anterior"
                  color={COMPARE_COLOR}
                  formatValue={(v) => fmtBrl(v)}
                  rows={f.por_carregador.map((c) => ({
                    key: c.hub_station_id,
                    label: c.carregador,
                    sublabel: `${fmtInt(c.recargas)} recargas · ${fmtKwhNum(c.kwh)} kWh`,
                    value: Number(c.receita_brl),
                    previous: c.receita_anterior_brl == null ? null : Number(c.receita_anterior_brl)
                  }))}
                />
              </div>
            </div>
          </div>

          <SectionTitle>Receita por mês</SectionTitle>
          <div className="rounded-3xl bg-white p-4 shadow-soft">
            {series.data && (
              <StackedBars
                ariaLabel="Receita por mês por origem"
                labels={series.data.labels}
                formatLabel={fmtMonth}
                formatValue={(v) => fmtBrl(v)}
                series={series.data.origens.map((og) => ({
                  key: og.origem,
                  label: og.label,
                  color: originColor(og.origem),
                  note: og.estimada ? 'estimada' : undefined,
                  values: (series.data!.receita_brl[og.origem] ?? []).map(Number)
                }))}
              />
            )}
          </div>

          <SectionTitle>Por origem</SectionTitle>
          <div className="overflow-x-auto rounded-3xl bg-white shadow-soft">
            <table className="w-full min-w-[420px] border-collapse text-[13.5px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.05em] text-bow-royal/55">
                  <th className="px-4 py-3 font-semibold">Origem</th>
                  <th className="px-3 py-3 text-right font-semibold">kWh</th>
                  <th className="px-3 py-3 text-right font-semibold">Receita</th>
                  <th className="px-4 py-3 text-right font-semibold">Anterior</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {f.totais.map((t) => {
                  const ant = a?.totais.find((x) => x.origem === t.origem);
                  return (
                    <tr key={t.origem} className="border-t border-bow-royal/[0.07]">
                      <td className="px-4 py-3 font-semibold">
                        <span
                          aria-hidden
                          className="mr-2 inline-block h-2.5 w-2.5 rounded-[3px] align-middle"
                          style={{ background: originColor(t.origem) }}
                        />
                        {t.label}
                        {t.estimada && <EstimatedTag />}
                      </td>
                      <td className="px-3 py-3 text-right">{fmtKwhNum(t.kwh)}</td>
                      <td className="px-3 py-3 text-right">{fmtBrl(t.receita_brl)}</td>
                      <td className="px-4 py-3 text-right text-bow-royal/60">
                        {a ? fmtBrl(ant?.receita_brl ?? 0) : '—'}
                      </td>
                    </tr>
                  );
                })}
                {f.totais.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-4 text-bow-royal/55">
                      Nenhuma recarga no período.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {f.linhas.length > 0 && (
            <>
              <SectionTitle>Mês a mês</SectionTitle>
              <div className="overflow-x-auto rounded-3xl bg-white shadow-soft">
                <table className="w-full min-w-[420px] border-collapse text-[13px]">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.05em] text-bow-royal/55">
                      <th className="px-4 py-3 font-semibold">Mês</th>
                      <th className="px-3 py-3 font-semibold">Origem</th>
                      <th className="px-3 py-3 text-right font-semibold">kWh</th>
                      <th className="px-4 py-3 text-right font-semibold">Receita</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {f.linhas.map((l) => (
                      <tr key={`${l.mes}-${l.origem}`} className="border-t border-bow-royal/[0.07]">
                        <td className="px-4 py-2.5">{fmtMonth(l.mes)}</td>
                        <td className="px-3 py-2.5">
                          {l.label}
                          {l.estimada && <EstimatedTag />}
                        </td>
                        <td className="px-3 py-2.5 text-right">{fmtKwhNum(l.kwh)}</td>
                        <td className="px-4 py-2.5 text-right">{fmtBrl(l.receita_brl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <p className="m-0 mt-3 text-[11.5px] leading-snug text-bow-royal/50">
            Valores brutos: ainda sem custo de energia, taxas, cashback e repasse. Roaming é estimado pela
            tarifa contratada com o operador. "Anterior" = período imediatamente anterior, de mesmo
            tamanho, dentro do seu acesso; sem acesso naquela janela, aparece "—". Se o seu acesso cobre só
            parte da janela anterior, a variação em % não é mostrada ("base parcial").
          </p>
        </div>
      )}
    </div>
  );
}
